import { useQuery } from "@tanstack/react-query";
import { useState, useCallback, useMemo, useRef } from "react";
import { searchService } from "../../api/search";
import { SearchFilters, Property } from "../../types/search";
import { analyticsService } from "../../services/analyticsService";
import { useTenant } from "../../contexts/TenantContext";

const PAGE_SIZE = 20;

export const useSearchQuery = (pageSize = 20) => {
  const { config } = useTenant();
  const [filters, setFilters] = useState<SearchFilters & { number?: number }>(
    () => ({
      query: "",
      location: { city: "", state: "" },
      tenant_id: "",
    })
  );
  const [currentPage, setCurrentPage] = useState(1);

  const {
    data,
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["properties", filters],
    queryFn: async ({ signal }) => {
      const response = await searchService.searchProperties(
        {
          ...filters,
          number: filters.number || 1000, // Use specified number or default to 1000
        },
        0,
        PAGE_SIZE,
        signal
      );
      
      // Track search event
      if (filters.query || filters.location?.city) {
        analyticsService.trackEvent({
          tenantId: config?.id || filters.tenant_id,
          orgId: config?.orgData?.id,
          eventType: "search",
          category: "Search",
          action: "Search Query",
          label: filters.query || filters.location?.city || "browse",
          value: response.totalMatches,
          data: {
            searchQuery: filters.query,
            searchFilters: filters,
            resultCount: response.totalMatches,
            city: filters.location?.city,
            state: filters.location?.state,
          }
        });
      }
      
      return {
        properties: response.properties,
        totalAvailable: response.totalMatches,
        timestamp: response.timestamp,
      };
    },
    enabled: !!filters.tenant_id, // Only run query if tenant_id is set
  });

  // Keep track of previous results to display during loading
  const previousResultsRef = useRef<Property[]>([]);

  // Client-side pagination with memoization
  const paginatedResults = useMemo(() => {
    // If we have data, update the previous results reference
    if (data?.properties) {
      previousResultsRef.current = data.properties.slice(
        0,
        currentPage * pageSize
      );
      return previousResultsRef.current;
    }

    // During loading, return previous results if we're not on the first page
    // This prevents clearing the UI when loading new results
    if (loading && previousResultsRef.current.length > 0) {
      return previousResultsRef.current;
    }

    // Default case: return empty array or data if available
    return data?.properties?.slice(0, currentPage * pageSize) || [];
  }, [data?.properties, currentPage, pageSize, loading]);

  const totalAvailable = data?.totalAvailable || 0;
  const hasMore = paginatedResults.length < (data?.properties.length || 0);

  const search = useCallback((newFilters: SearchFilters, number?: number) => {
    setCurrentPage(1); // Reset to first page on new search
    setFilters({
      ...newFilters,
      number: number || 1000, // Default to 1000 if not specified
    });
  }, []);

  const loadMore = useCallback(() => {
    setCurrentPage((prev) => prev + 1);
  }, []);

  const retryFailedRequest = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    results: paginatedResults,
    loading,
    error: error ? (error as Error).message : null,
    hasMore,
    filters,
    search,
    loadMore,
    totalAvailable,
    retryFailedRequest,
    timestamp: data?.timestamp,
  };
};
